---
title: "The HAVEN IAQ Equipment Finder"
slug: "iaq-equipment-finder"
date: "2026-05-14"
author: "HAVEN Team"
category: "Announcements"
readTime: "4 min read"
excerpt: "North America's largest database of residential IAQ equipment - spec sheets, install manuals, HVI-certified fan performance data, every major brand."
featuredImage: "/blog/iaq-equipment-finder.webp"
---

import equipmentFinderImg from '../../assets/images/hangout/equipment-finder.png';
import equipmentResultsImg from '../../assets/images/hangout/equipment-results.png';

We're excited to announce the **HAVEN IAQ Equipment Finder** - what we believe is the largest database of residential indoor air quality equipment in North America. It's built for the people who actually specify and install this equipment, and it's free with a HAVEN Pro account.

## Spec sheets, install manuals, and HVI performance data

Every listing pairs the product with what you need to actually choose and install it: full **spec sheets**, manufacturer **install manuals**, and **fan performance data from the HVI Institute** - with representation from all major brands and product lines. The point is to let you compare equipment on evidence, not brochures.

<Figure
  src={equipmentFinderImg}
  alt="The HAVEN IAQ Equipment Finder - browse residential IAQ equipment"
  caption="Start in the Equipment Finder and narrow to the products that fit the job."
/>

## Built brand-agnostic

HAVEN sits in a rare position in this industry: we're agnostic about which brands and products you use. We don't sell the equipment, so we have no reason to play favorites. What we care about is whether a solution actually solves the air quality problems we monitor and control.

And because we're data-driven, we have a front-row seat to the whole picture - which solutions our professional installers choose, and how those solutions actually perform once they're in a home, over time. It's the same evidence-first approach behind our [active air management](/active-air-management) platform, and the Equipment Finder turns that vantage point into something every contractor can use.

## Why electronic air cleaners aren't included

What we left out is as deliberate as what we put in. We've intentionally kept scientifically unproven devices out of the tool - **electronic air cleaners** being the clearest example. The body of research on them not only fails to build consensus around their claimed benefits, it also points to real potential for harm: ozone production, unintended chemistry in the home, and other byproducts.

<Callout title="An honest caveat">
We don't independently verify every device on the site, and a product's inclusion isn't an endorsement by HAVEN. The tool is a starting point for informed decisions - not a substitute for them.
</Callout>

## What IAQ equipment the Finder covers

Our goal is to represent the majority of what's actually on the market across the four core categories:

- **Ventilation** - ERVs, HRVs, supply and exhaust. (See our take on [residential demand control ventilation](/residential-demand-control-ventilation) for how we think it should be controlled.)
- **Filtration** - media filters and whole-home solutions
- **Dehumidification** - whole-home dehumidifiers
- **Humidification** - humidifiers matched to climate and system

We also want to expose contractors to specialized solutions they may not know exist - **activated carbon**, **bypass HEPA**, and **inline filtration** that can be added to ERVs and HRVs to condition the outdoor air entering the home.

<Figure
  src={equipmentResultsImg}
  alt="HAVEN equipment results with HVI-certified performance data, side by side"
  caption="Compare HVI-certified equipment side by side, with the spec data in view."
  maxWidth="560px"
/>

## Find it with a conversation

We've also added an AI feature that makes the whole tool simpler to use. Describe the solution you're looking for in plain language, and it applies the right filters to narrow things down - then pulls out the highlights of the products it finds, so you can compare options without digging through every spec sheet yourself.

## A living tool - tell us what to fix

This database will only get better with input from the field. We're open to continuous feedback on the products listed here: what we should add, what we should remove, and what we should fix. If you specify or install this equipment for a living, your input makes it better for everyone.

## Why we built it

For too long, IAQ equipment has been picked by what's on the truck, what a rep dropped off last, or which brochure made the boldest claim. The industry - and the homeowners it serves - deserve better than that.

The Equipment Finder is our step toward making **data-driven IAQ the default, not the exception**: the right equipment specified because it's the right equipment. That's the change we're after.

The IAQ Equipment Finder is available now, free with a HAVEN Pro account, at [pro.haveniaq.com/resources](https://pro.haveniaq.com/resources). New to HAVEN? See [how our solutions fit together](/solutions) or [become a HAVEN Pro](/become-a-pro).

## Frequently asked questions

<FAQ items={[
  {
    question: "Is the Equipment Finder free?",
    answer: "Yes. It's included free with a HAVEN Pro account, alongside the rest of the Pro Portal tools.",
  },
  {
    question: "Which brands does it include?",
    answer: "It represents all major brands and product lines across ventilation, filtration, dehumidification, and humidification - plus specialized solutions like activated carbon and bypass HEPA.",
  },
  {
    question: "Why aren't electronic air cleaners included?",
    answer: "We've intentionally left out devices the research doesn't support. With electronic air cleaners, the science doesn't establish the claimed benefits and points to real risks like ozone production - so they're out of the tool.",
  },
  {
    question: "Does HAVEN endorse the products listed?",
    answer: "No. Inclusion isn't an endorsement, and we don't independently verify every device. The Finder is a starting point for an informed decision, not a substitute for one.",
  },
]} />
